import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

function fmt(n) { return (n||0).toLocaleString("fr-FR", { maximumFractionDigits:0 }); }
function fmtD(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }

const STEPS = [
  { n:1, label:"Sélection" },
  { n:2, label:"Analyse" },
  { n:3, label:"Doublons" },
  { n:4, label:"Écritures" },
  { n:5, label:"Finalisation" },
];

function StepIndicator({ step }) {
  return (
    <div style={{ display:"flex", gap:0, marginBottom:24, overflowX:"auto" }}>
      {STEPS.map((s,i) => (
        <div key={s.n} style={{ display:"flex", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderRadius:8,
            background: step===s.n ? "var(--g)" : "transparent",
            color:      step===s.n ? "#fff" : "var(--text-muted)",
          }}>
            <div style={{ width:22, height:22, borderRadius:"50%",
              background: step===s.n ? "rgba(255,255,255,0.3)" : step>s.n ? "var(--g)" : "#e5e7eb",
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700,
              color: step>s.n ? "#fff" : step===s.n ? "#fff" : "#6b7280",
            }}>{step>s.n ? "✓" : s.n}</div>
            <span style={{ fontSize:13, fontWeight:step===s.n?700:400, whiteSpace:"nowrap" }}>{s.label}</span>
          </div>
          {i < STEPS.length-1 && <div style={{ width:24, height:1, background:"var(--border)" }} />}
        </div>
      ))}
    </div>
  );
}

export default function Fusion({ companies = [], currentCompanyId, onNavigate }) {
  const [step, setStep]         = useState(1);
  const [merger, setMerger]     = useState(null);
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [executing, setExec]    = useState(false);
  const [completed, setCompleted] = useState(false);

  // Étape 1 : sélection
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [mergeDate, setMergeDate] = useState("2026-03-31");
  const [notes, setNotes]       = useState("");

  // Étape 3 : gestion doublons
  const [dupDecisions, setDupDecisions] = useState({});

  const activeCompanies = companies.filter(c => !c.archived);

  const loadMergers = useCallback(() => {
    api.mergers.list().then(list => {
      const existing = list.find(m => m.status !== "completed" && m.status !== "cancelled");
      if (existing) {
        setMerger(existing);
        setSourceId(String(existing.source_company_id));
        setTargetId(String(existing.target_company_id));
        setMergeDate(existing.merge_date||"");
        setNotes(existing.notes||"");
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { loadMergers(); }, [loadMergers]);

  const handleCreateOrUpdateMerger = async () => {
    if (!sourceId || !targetId) { setError("Sélectionnez les deux sociétés."); return; }
    if (sourceId === targetId) { setError("La société source et la cible ne peuvent pas être identiques."); return; }
    setError("");
    try {
      let m;
      if (merger) {
        m = await api.mergers.update(merger.id, { merge_date: mergeDate, notes });
      } else {
        m = await api.mergers.create({ source_company_id: sourceId, target_company_id: targetId, merge_date: mergeDate, notes });
      }
      setMerger(m);
      setStep(2);
    } catch(e) { setError(e.message); }
  };

  const loadPreview = async () => {
    if (!sourceId || !targetId) return;
    setLoading(true); setError("");
    try {
      const p = await api.mergers.preview(sourceId, targetId);
      setPreview(p);
      const dups = {};
      (p.analysis?.clients?.duplicates||[]).forEach(c => { dups[c.client_name] = "merge"; });
      setDupDecisions(dups);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const executeMerger = async () => {
    if (!merger) return;
    const confirmed = window.confirm(
      `⚠️ ATTENTION — Action irréversible\n\n` +
      `La société "${preview?.source?.name}" sera archivée.\n` +
      `Toutes ses données seront transférées vers "${preview?.target?.name}".\n\n` +
      `Confirmer la fusion ?`
    );
    if (!confirmed) return;
    setExec(true); setError("");
    try {
      const result = await api.mergers.execute(merger.id);
      setCompleted(true);
      setMerger({ ...merger, status:"completed", transfer_summary: result.summary });
    } catch(e) { setError(e.message); }
    finally { setExec(false); }
  };

  const sourceName = companies.find(c => String(c.id) === String(sourceId))?.name || "Société absorbée";
  const targetName = companies.find(c => String(c.id) === String(targetId))?.name || "Société absorbante";

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Fusion de sociétés</h2>
        {merger && (
          <span style={{ fontSize:12, padding:"4px 10px", borderRadius:10,
            background: merger.status==="completed" ? "#d1fae5" : "#e0e7ff",
            color:      merger.status==="completed" ? "#065f46" : "#3730a3",
          }}>
            {{planned:"Planifiée",in_progress:"En cours",completed:"Terminée"}[merger.status]}
          </span>
        )}
      </div>

      <StepIndicator step={step} />

      {error && <div style={{ padding:"10px 14px", background:"#dc262610", border:"1px solid #dc262630", borderRadius:8, color:"#dc2626", marginBottom:12, fontSize:13 }}>{error}</div>}

      {/* ── ÉTAPE 1 : Sélection ──────────────────────────────────── */}
      {step === 1 && (
        <div className="g2">
          <div className="card">
            <div className="card-header"><span className="card-title">🔀 Configuration de la fusion</span></div>
            <div className="form-group">
              <label className="form-label">Société absorbée (disparaît après fusion)</label>
              <select className="form-control" value={sourceId} onChange={e => setSourceId(e.target.value)} disabled={!!merger}>
                <option value="">— Sélectionner —</option>
                {activeCompanies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
              </select>
            </div>
            <div style={{ textAlign:"center", fontSize:20, color:"var(--text-muted)", margin:"4px 0" }}>↓</div>
            <div className="form-group">
              <label className="form-label">Société absorbante (récupère tout)</label>
              <select className="form-control" value={targetId} onChange={e => setTargetId(e.target.value)} disabled={!!merger}>
                <option value="">— Sélectionner —</option>
                {activeCompanies.filter(c => String(c.id) !== String(sourceId)).map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date d'effet de la fusion</label>
              <input className="form-control" type="date" value={mergeDate} onChange={e => setMergeDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes juridiques / contexte</label>
              <textarea className="form-control" value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                placeholder="Ex: Fusion simplifiée — absorption totale par décision de l'assemblée générale du 15/03/2026" />
            </div>
            <button className="btn primary" style={{ width:"100%" }} onClick={handleCreateOrUpdateMerger}>
              {merger ? "Passer à l'analyse →" : "Planifier et analyser →"}
            </button>
          </div>

          {merger && (
            <div className="card">
              <div className="card-header"><span className="card-title">📋 Fusion en cours</span></div>
              <div style={{ display:"flex", flexDirection:"column", gap:10, fontSize:13 }}>
                <div><span style={{ color:"var(--text-muted)" }}>Absorbée : </span><strong>{merger.source_name}</strong></div>
                <div><span style={{ color:"var(--text-muted)" }}>Absorbante : </span><strong>{merger.target_name}</strong></div>
                <div><span style={{ color:"var(--text-muted)" }}>Date d'effet : </span><strong>{fmtD(merger.merge_date)}</strong></div>
                <div><span style={{ color:"var(--text-muted)" }}>Planifiée le : </span>{fmtD(merger.created_at)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ÉTAPE 2 : Analyse ────────────────────────────────────── */}
      {step === 2 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:13, color:"var(--text-muted)" }}>Rapport d'analyse pré-fusion : <strong>{sourceName}</strong> → <strong>{targetName}</strong></div>
            {!preview && <button className="btn primary" onClick={loadPreview} disabled={loading}>{loading?"Analyse…":"🔍 Analyser"}</button>}
          </div>

          {loading && <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Analyse en cours…</div>}

          {preview && (
            <>
              {/* Actif net */}
              <div className="kpi-grid k4">
                <div className="kpi"><div className="kpi-label">Actif net transféré</div><div className="kpi-value" style={{ color:"var(--g)" }}>{fmt(preview.analysis.net_assets)} €</div></div>
                <div className="kpi"><div className="kpi-label">Solde bancaire</div><div className="kpi-value">{fmt(preview.analysis.bank_balance)} €</div></div>
                <div className="kpi"><div className="kpi-label">Créances clients</div><div className="kpi-value">{fmt(preview.analysis.receivables)} €</div></div>
                <div className="kpi"><div className="kpi-label">Dettes fournisseurs</div><div className="kpi-value" style={{ color:"var(--red)" }}>{fmt(preview.analysis.payables)} €</div></div>
              </div>

              <div className="g2">
                {/* Éléments à transférer */}
                <div className="card">
                  <div className="card-header"><span className="card-title">📦 Éléments à transférer</span></div>
                  {[
                    { label:"Factures en attente", value:`${fmt(preview.analysis.pending_invoices.count)} factures · ${fmt(preview.analysis.pending_invoices.total)} € TTC`, icon:"📄" },
                    { label:"Écritures comptables", value:`${fmt(preview.analysis.transactions)} écritures`, icon:"📈" },
                    { label:"Stocks", value:`${fmt(preview.analysis.stocks.count)} produits · ${fmt(preview.analysis.stocks.value)} €`, icon:"📦" },
                    { label:"Abonnements", value:`${fmt(preview.analysis.subscriptions)} abonnements actifs`, icon:"🔄" },
                    { label:"Notes de frais", value:`${fmt(preview.analysis.expenses)} notes`, icon:"🧾" },
                  ].map(item => (
                    <div key={item.label} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"0.5px solid var(--border)" }}>
                      <span style={{ fontSize:18 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600 }}>{item.label}</div>
                        <div style={{ fontSize:12, color:"var(--text-muted)" }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Clients */}
                <div className="card">
                  <div className="card-header"><span className="card-title">👥 Clients</span></div>
                  <div style={{ display:"flex", gap:16, marginBottom:12 }}>
                    <div className="kpi"><div className="kpi-label">De {sourceName}</div><div className="kpi-value">{preview.analysis.clients.source}</div></div>
                    <div className="kpi"><div className="kpi-label">De {targetName}</div><div className="kpi-value">{preview.analysis.clients.target}</div></div>
                    <div className="kpi"><div className="kpi-label">Doublons</div><div className="kpi-value" style={{ color:"var(--amber)" }}>{preview.analysis.clients.common}</div></div>
                  </div>
                  {preview.analysis.clients.duplicates?.length > 0 && (
                    <div style={{ fontSize:12, color:"var(--text-muted)" }}>
                      {preview.analysis.clients.duplicates.slice(0,5).map(c => c.client_name).join(", ")}
                      {preview.analysis.clients.duplicates.length > 5 ? ` … +${preview.analysis.clients.duplicates.length-5}` : ""}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <button className="btn" onClick={() => setStep(1)}>← Retour</button>
                <button className="btn primary" onClick={() => setStep(3)}>Gérer les doublons →</button>
              </div>
            </>
          )}

          {!preview && !loading && (
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn" onClick={() => setStep(1)}>← Retour</button>
            </div>
          )}
        </div>
      )}

      {/* ── ÉTAPE 3 : Doublons ───────────────────────────────────── */}
      {step === 3 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">👥 Résolution des doublons clients</span></div>
            {!preview?.analysis?.clients?.duplicates?.length ? (
              <div style={{ textAlign:"center", padding:32, color:"var(--text-muted)" }}>
                <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
                <div>Aucun client en doublon détecté.</div>
              </div>
            ) : (
              <>
                <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:12 }}>
                  {preview.analysis.clients.common} client(s) présent(s) dans les deux sociétés. Choisissez l'action pour chacun.
                </p>
                <table className="tbl" style={{ marginBottom:12 }}>
                  <thead><tr><th>Client</th><th>Email</th><th>Action</th></tr></thead>
                  <tbody>
                    {preview.analysis.clients.duplicates.map(c => (
                      <tr key={c.client_name}>
                        <td style={{ fontWeight:600 }}>{c.client_name}</td>
                        <td style={{ fontSize:12, color:"var(--text-muted)" }}>{c.client_email||"—"}</td>
                        <td>
                          <select className="form-control" style={{ fontSize:12, padding:"3px 8px" }}
                            value={dupDecisions[c.client_name]||"merge"}
                            onChange={e => setDupDecisions(p => ({...p, [c.client_name]: e.target.value}))}>
                            <option value="merge">Fusionner les fiches</option>
                            <option value="keep_both">Garder les deux séparées</option>
                            <option value="keep_target">Conserver celle de {targetName}</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button className="btn" onClick={() => setStep(2)}>← Retour</button>
              <button className="btn primary" onClick={() => setStep(4)}>Écritures comptables →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 4 : Écritures comptables ───────────────────────── */}
      {step === 4 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">📝 Écritures comptables de fusion</span></div>
            <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:16 }}>
              Les écritures suivantes seront générées automatiquement lors de l'exécution.
            </p>

            {/* Chez la source */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", marginBottom:8 }}>
                Chez {sourceName} (clôture)
              </div>
              <table className="tbl">
                <thead><tr><th>Journal</th><th>Compte</th><th>Libellé</th><th>Débit</th><th>Crédit</th></tr></thead>
                <tbody>
                  <tr>
                    <td><span style={{ fontSize:11, fontFamily:"monospace" }}>OD</span></td>
                    <td><span style={{ fontFamily:"monospace" }}>1040</span></td>
                    <td>Capital social — Dissolution</td>
                    <td style={{ color:"var(--red)" }}>{preview ? fmt(preview.analysis.net_assets) : "…"} €</td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td><span style={{ fontSize:11, fontFamily:"monospace" }}>OD</span></td>
                    <td><span style={{ fontFamily:"monospace" }}>4561</span></td>
                    <td>Associés — apport fusion</td>
                    <td>—</td>
                    <td style={{ color:"var(--g)" }}>{preview ? fmt(preview.analysis.net_assets) : "…"} €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Chez la cible */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", marginBottom:8 }}>
                Chez {targetName} (reprise actifs/passifs)
              </div>
              <table className="tbl">
                <thead><tr><th>Journal</th><th>Compte</th><th>Libellé</th><th>Débit</th><th>Crédit</th></tr></thead>
                <tbody>
                  <tr>
                    <td><span style={{ fontSize:11, fontFamily:"monospace" }}>OD</span></td>
                    <td><span style={{ fontFamily:"monospace" }}>1068</span></td>
                    <td>Autres réserves — Fusion {sourceName}</td>
                    <td>—</td>
                    <td style={{ color:"var(--g)" }}>{preview ? fmt(preview.analysis.net_assets) : "…"} €</td>
                  </tr>
                  <tr>
                    <td><span style={{ fontSize:11, fontFamily:"monospace" }}>OD</span></td>
                    <td><span style={{ fontFamily:"monospace" }}>512</span></td>
                    <td>Banque — Reprise solde {sourceName}</td>
                    <td style={{ color:"var(--red)" }}>{preview ? fmt(preview.analysis.bank_balance) : "…"} €</td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ padding:"10px 14px", background:"#fef3c7", border:"1px solid #fbbf2440", borderRadius:8, fontSize:12, color:"#92400e", marginBottom:12 }}>
              ⚠️ Ces écritures sont générées automatiquement. Une validation par votre expert-comptable est recommandée avant export FEC.
            </div>

            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button className="btn" onClick={() => setStep(3)}>← Retour</button>
              <button className="btn primary" onClick={() => setStep(5)}>Finaliser →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ÉTAPE 5 : Finalisation ───────────────────────────────── */}
      {step === 5 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {!completed ? (
            <>
              <div className="card">
                <div className="card-header"><span className="card-title">⚡ Résumé de la fusion</span></div>
                <div style={{ display:"flex", flexDirection:"column", gap:10, fontSize:13, marginBottom:16 }}>
                  <div style={{ padding:"12px 14px", background:"#f8f9fa", borderRadius:8 }}>
                    <div style={{ fontWeight:700, marginBottom:6 }}>Société absorbée</div>
                    <div>{sourceName} → sera archivée après la fusion</div>
                  </div>
                  <div style={{ padding:"12px 14px", background:"rgba(26,158,114,0.07)", borderRadius:8 }}>
                    <div style={{ fontWeight:700, marginBottom:6 }}>Société absorbante</div>
                    <div>{targetName} → récupère toutes les données</div>
                  </div>
                  <div>📅 Date d'effet : <strong>{fmtD(mergeDate)}</strong></div>
                  {preview && (
                    <div>💰 Actif net transféré : <strong style={{ color:"var(--g)" }}>{fmt(preview.analysis.net_assets)} €</strong></div>
                  )}
                </div>

                <div style={{ padding:"12px 14px", background:"#dc262610", border:"1px solid #dc262630", borderRadius:8, fontSize:13, marginBottom:16 }}>
                  <strong>⚠️ Action irréversible</strong><br />
                  L'exécution de la fusion archivera définitivement {sourceName}. Cette opération ne peut pas être annulée.
                  Assurez-vous d'avoir fait une sauvegarde de la base de données avant de continuer.
                </div>

                <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                  <button className="btn" onClick={() => setStep(4)}>← Retour</button>
                  <button className="btn" style={{ background:"#dc2626", color:"#fff", border:"none" }}
                    onClick={executeMerger} disabled={executing}>
                    {executing ? "Fusion en cours…" : `⚡ Exécuter la fusion du ${fmtD(mergeDate)}`}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="card" style={{ textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
              <div style={{ fontSize:18, fontWeight:700, marginBottom:8 }}>Fusion réalisée avec succès</div>
              <div style={{ fontSize:13, color:"var(--text-muted)", marginBottom:20 }}>
                {sourceName} a été archivée. Toutes ses données ont été transférées vers {targetName}.
              </div>
              {merger?.transfer_summary && (
                <div className="kpi-grid k4" style={{ marginBottom:20 }}>
                  {[
                    ["Factures",    merger.transfer_summary.invoices],
                    ["Écritures",   merger.transfer_summary.transactions],
                    ["Comptes",     merger.transfer_summary.bank_accounts],
                    ["Abonnements", merger.transfer_summary.subscriptions],
                  ].map(([k,v]) => (
                    <div className="kpi" key={k}><div className="kpi-label">{k}</div><div className="kpi-value">{fmt(v)}</div></div>
                  ))}
                </div>
              )}
              <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                <button className="btn" onClick={() => onNavigate?.("migration")}>Tableau migration</button>
                <button className="btn primary" onClick={() => onNavigate?.("fec")}>Exporter FEC →</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
